import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { AppHeader } from "@/components/observer/AppHeader";
import { ScoreItem } from "@/components/scoring/ScoreItem";
import { ResultsPanel } from "@/components/scoring/ResultsPanel";
import { api, errMsg } from "@/lib/api";
import { labelCls } from "@/lib/ui";

const SCORER_KEY = "observer.scorer";

const ScorerTab = ({ id, active, name, progress, total, onSelect, onRename }) => {
  const [edit, setEdit] = useState(false);
  const [v, setV] = useState(name);
  useEffect(() => setV(name), [name]);
  return (
    <div className={`flex items-center gap-3 px-4 h-12 border transition-colors ${active ? "border-[var(--o-fg)] bg-[var(--o-surface)]" : "border-[var(--o-border)] hover:border-[var(--o-border-strong)]"}`} data-testid={`scorer-tab-${id}`}>
      {edit ? (
        <input autoFocus value={v} onChange={(e) => setV(e.target.value)} onBlur={() => { setEdit(false); onRename(v); }} onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()} className="bg-transparent outline-none text-sm font-semibold w-32 border-b border-[var(--o-fg)]" data-testid={`scorer-name-input-${id}`} />
      ) : (
        <button onClick={onSelect} onDoubleClick={() => setEdit(true)} className="text-sm font-semibold" title="Double-click to rename" data-testid={`scorer-select-${id}`}>{name}</button>
      )}
      <span className="font-mono text-[11px] text-[var(--o-muted2)]">{progress}/{total}</span>
      {active && !edit && <button onClick={() => setEdit(true)} className="font-mono text-[10px] text-[var(--o-muted3)] hover:text-[var(--o-fg)]" data-testid={`scorer-rename-${id}`}>rename</button>}
    </div>
  );
};

export default function Scoring() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [scorer, setScorer] = useState(() => localStorage.getItem(SCORER_KEY) || "a");
  const [forceReveal, setForceReveal] = useState(false);

  const load = useCallback(() => api.get(`/sessions/${id}/scoring`).then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e))), [id]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => localStorage.setItem(SCORER_KEY, scorer), [scorer]);

  const put = async (body) => {
    try {
      const { data: d } = await api.put(`/sessions/${id}/scoring`, { scorer, ...body });
      setData(d);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  if (!data) return <div className="min-h-screen bg-[var(--o-bg)]" data-testid="scoring-loading" />;
  const other = scorer === "a" ? "b" : "a";
  const reveal = data.summary.complete || forceReveal;
  const mine = data.scorers[scorer].scores;
  const theirs = data.scorers[other].scores;

  return (
    <div className="noise min-h-screen bg-[var(--o-bg)] text-[var(--o-fg)]" data-testid="scoring-page">
      <AppHeader />
      <main className="relative z-10 max-w-7xl px-6 sm:px-10 pb-32">
        <div className="pt-12 pb-8 rise">
          <p className={labelCls}>Scoring · {data.mode} · {data.id}</p>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight mt-3">{data.title}</h1>
          <p className="text-sm sm:text-base text-[var(--o-muted)] mt-4 max-w-2xl">Each person scores independently on their own device. <span className="font-mono text-[var(--o-fg)]">3</span> would have read it aloud · <span className="font-mono text-[var(--o-fg)]">2</span> true and grounded, but the room knew it · <span className="font-mono text-[var(--o-fg)]">1</span> generic, wrong or ungrounded.</p>
        </div>
        <div className="flex flex-wrap gap-2 mb-6">
          {["a", "b"].map((k) => (
            <ScorerTab key={k} id={k} active={scorer === k} name={data.scorers[k].name} progress={data.summary.progress[k]} total={data.summary.items} onSelect={() => setScorer(k)} onRename={(name) => put({ name, scorer: k })} />
          ))}
        </div>
        <div className="grid lg:grid-cols-[1fr_340px] gap-6 items-start">
          <div className="space-y-3" data-testid="score-items">
            {data.items.length === 0 && <p className="text-sm text-[var(--o-muted3)]" data-testid="score-empty">No answers or shown flags in this session yet.</p>}
            {data.items.map((it) => (
              <ScoreItem key={`${scorer}-${it.key}`} item={it} mine={mine[it.key]} other={theirs[it.key]} otherName={data.scorers[other].name} reveal={reveal} onScore={(key, score, note) => put({ key, score, note })} />
            ))}
          </div>
          <div className="lg:sticky lg:top-6">
            <ResultsPanel summary={data.summary} scorers={data.scorers} reveal={reveal} onReveal={() => setForceReveal(true)} />
          </div>
        </div>
      </main>
    </div>
  );
}
